import { click1, click2 } from './click_audio_base64';
import fixIOSAudioApiPlayback from '../../../utils/platform/fixIOSAudioApiPlayback';

const SCHEDULING_LOOKAHEAD = 200;

export default class MetronomePlayer {
  private audioContext: AudioContext | null = null;

  private click1Buffer: AudioBuffer | null = null;

  private click2Buffer: AudioBuffer | null = null;

  private animationFrameId: number | null = null;

  private nextTickTime: number = 0;

  private tickScheduled: boolean = false;

  private currentBeat: number = 0;

  private beatInterval: number = 0;

  constructor(
    private bpm: number,
    private timeSig: number,
    private onBeatChange: (beat: number, offset: number) => void
  ) {
    this.init();
  }

  private async init() {
    fixIOSAudioApiPlayback();

    this.beatInterval = 60000 / this.bpm;
    this.audioContext = new AudioContext();

    const loadAudioBuffer = async (base64: string) => {
      const response = await fetch(base64);
      const arrayBuffer = await response.arrayBuffer();
      return this.audioContext?.decodeAudioData(arrayBuffer);
    };

    loadAudioBuffer(click1).then((buffer) => {
      this.click1Buffer = buffer;
    });
    loadAudioBuffer(click2).then((buffer) => {
      this.click2Buffer = buffer;
    });
  }

  start() {
    if (this.animationFrameId === null) {
      this.nextTickTime = this.audioContext!.currentTime * 1000;
      this.currentBeat = this.timeSig - 1;
      this.tickUpdate();
    }
  }

  private tickUpdate = () => {
    const now = this.audioContext!.currentTime * 1000;

    if (now >= this.nextTickTime - SCHEDULING_LOOKAHEAD && !this.tickScheduled) {
      const startIn = Math.max(0, (this.nextTickTime - now) / 1000);
      const isDownbeat = this.currentBeat === this.timeSig - 1;
      this.playTick(
        isDownbeat ? this.click2Buffer : this.click1Buffer,
        startIn,
        isDownbeat ? 1.0 : 0.5
      );
      this.tickScheduled = true;
    }

    if (now >= this.nextTickTime) {
      const nextBeat = (this.currentBeat + 1) % this.timeSig;
      this.currentBeat = nextBeat;
      const rawOffset = now - this.nextTickTime;
      let adjustedOffset = rawOffset;
      if (this.audioContext) {
        const baseLatency = this.audioContext.baseLatency || 0;
        const outputLatency = this.audioContext.outputLatency || 0;
        const totalOutMs = (baseLatency + outputLatency) * 1000;
        adjustedOffset = rawOffset - totalOutMs;
      }
      this.onBeatChange(nextBeat, adjustedOffset);
      // in case tab lost focus we will have skipped beats
      const beatsDiff = Math.floor((now - this.nextTickTime) / this.beatInterval);
      this.nextTickTime += (beatsDiff + 1) * this.beatInterval;
      this.tickScheduled = false;
    }

    this.animationFrameId = requestAnimationFrame(this.tickUpdate);
  };

  private playTick(buffer: AudioBuffer | null, startIn: number, gain: number = 1.0) {
    if (this.audioContext && buffer) {
      const source = this.audioContext.createBufferSource();
      source.buffer = buffer;
      
      const gainNode = this.audioContext.createGain();
      gainNode.gain.value = gain;
      
      source.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      source.start(this.audioContext.currentTime + startIn);
    }
  }

  stop() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.currentBeat = 0;
  }

  updateBpm(bpm: number) {
    this.bpm = bpm;
    this.beatInterval = 60000 / this.bpm;
  }

  updateTimeSig(timeSig: number) {
    this.timeSig = timeSig;
  }

  setOnBeatChangeCallback(callback: (beat: number, offset: number) => void) {
    this.onBeatChange = callback;
  }

  destroy() {
    this.stop();
    this.audioContext?.close();
  }
}
